(() => {
  const getMagazines = () => {
    const activeIssueMagazines = Array.from(document.querySelectorAll('.issues-reader__issue--active .magazine')).filter(
      (magazine) => magazine instanceof HTMLElement
    );
    if (activeIssueMagazines.length > 0) return activeIssueMagazines;

    return Array.from(document.querySelectorAll('.magazine')).filter((magazine) => magazine instanceof HTMLElement);
  };

  if (getMagazines().length === 0) return;

  let animationActive = true;
  /** @type {HTMLImageElement | null} */
  let previousPulseImage = null;
  const visibleImages = new Set();

  const applyLazyFallback = () => {
    if ('lazySizes' in window) return;

    const lazyImages = Array.from(document.querySelectorAll('img.lazyload[data-src]'));
    if (lazyImages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const img = entry.target;
          if (!(img instanceof HTMLImageElement)) return;

          img.classList.add('lazyloading');
          img.src = img.dataset.src || '';
          img.onload = () => {
            img.classList.remove('lazyloading');
            img.classList.add('lazyloaded');
          };

          obs.unobserve(img);
        });
      },
      {
        rootMargin: '300px 0px',
      }
    );

    lazyImages.forEach((img) => observer.observe(img));
  };

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const image = entry.target;
        if (!(image instanceof HTMLImageElement)) return;

        if (entry.intersectionRatio >= 0.5) {
          visibleImages.add(image);
        } else {
          visibleImages.delete(image);
        }
      });
    },
    {
      threshold: 0.5,
    }
  );

  const allImages = Array.from(document.querySelectorAll('.mag-img-wrapper img'));

  allImages.forEach((img) => {
    intersectionObserver.observe(img);

    img.addEventListener('click', () => {
      animationActive = false;

      const mobile = window.matchMedia('(max-width: 868px)').matches;
      const duration = mobile ? '400ms' : '600ms';
      const transition = `width ${duration} ease, max-height 0.65s cubic-bezier(0.22, 1, 0.36, 1)`;

      if (img.classList.contains('active')) {
        const currentWidth = window.getComputedStyle(img).width;
        img.style.transition = transition;
        img.style.width = currentWidth;
        img.classList.remove('active');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            img.style.removeProperty('width');
          });
        });
        return;
      }

      img.style.transition = transition;
      img.classList.add('active');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const mobile = window.matchMedia('(max-width: 868px)').matches;
    const duration = mobile ? '400ms' : '600ms';

    allImages.forEach((image) => {
      if (!image.classList.contains('active')) return;
      const currentWidth = window.getComputedStyle(image).width;
      image.style.transition = `width ${duration} ease, max-height 0.65s cubic-bezier(0.22, 1, 0.36, 1)`;
      image.style.width = currentWidth;
      image.classList.remove('active');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          image.style.removeProperty('width');
        });
      });
    });
  });

  const animateImage = () => {
    if (!animationActive) return;

    const candidates = Array.from(visibleImages).filter((img) => !img.classList.contains('active'));

    if (candidates.length > 0) {
      let targetImage = candidates[Math.floor(Math.random() * candidates.length)];

      if (candidates.length > 1 && targetImage === previousPulseImage) {
        const nextIndex = (candidates.indexOf(targetImage) + 1) % candidates.length;
        targetImage = candidates[nextIndex];
      }

      previousPulseImage = targetImage;
      targetImage.classList.add('pulse');

      const cleanup = () => {
        targetImage.classList.remove('pulse');
        targetImage.removeEventListener('animationend', cleanup);
        targetImage.removeEventListener('animationcancel', cleanup);
      };
      targetImage.addEventListener('animationend', cleanup);
      targetImage.addEventListener('animationcancel', cleanup);
    }

    window.setTimeout(animateImage, 2500);
  };

  const highlightChapter = () => {
    const middleScreen = window.innerHeight / 2 + window.scrollY;

    const magazines = getMagazines();

    magazines.forEach((magazine) => {
      const chapters = Array.from(magazine.querySelectorAll('.chapter')).filter((chapter) => chapter instanceof HTMLElement);
      const titles = Array.from(magazine.querySelectorAll('.chapter-title')).filter((title) => title instanceof HTMLElement);

      chapters.forEach((chapter, index) => {
        const start = chapter.offsetTop + magazine.offsetTop;
        const end = start + chapter.offsetHeight;

        if (middleScreen >= start && middleScreen <= end && titles[index]) {
          titles.forEach((title) => title.classList.remove('active'));
          titles[index].classList.add('active');

          const titlesContainer = titles[index].parentElement;
          let targetCenter = window.innerWidth / 2;

          if (titlesContainer instanceof HTMLElement) {
            const containerRect = titlesContainer.getBoundingClientRect();
            const chapterRect = chapter.getBoundingClientRect();
            const chapterCenterInViewport = chapterRect.left + chapterRect.width / 2;
            targetCenter = chapterCenterInViewport - containerRect.left;
          }

          const offset = titles[index].offsetLeft + titles[index].offsetWidth / 2;
          const shiftX = targetCenter - offset;

          titles.forEach((title) => {
            title.style.transform = `translateX(${shiftX}px)`;
          });
        }
      });
    });
  };

  /** @param {() => void} fn @param {number} wait */
  const throttle = (fn, wait) => {
    let waiting = false;

    return () => {
      if (waiting) return;
      waiting = true;

      fn();
      window.setTimeout(() => {
        waiting = false;
      }, wait);
    };
  };

  const throttledHighlightChapter = throttle(highlightChapter, 100);

  const coverSelectors = Array.from(document.querySelectorAll('[data-cover-selector]'));
  coverSelectors.forEach((selector) => {
    const options = Array.from(selector.querySelectorAll('[data-cover-option]'));
    selector.classList.toggle('magazine-covers--many-options', options.length > 4);

    const form = selector.querySelector('.magazine-covers__form');
    const addButton = form instanceof HTMLFormElement ? form.querySelector('.magazine-covers__add-button') : null;
    const variantInput = selector.querySelector('[data-cover-variant-input]');
    const quantityInput = selector.querySelector('[data-cover-quantity-input]');
    const quantityValue = selector.querySelector('[data-quantity-value]');
    const quantityDecrease = selector.querySelector('[data-quantity-decrease]');
    const quantityIncrease = selector.querySelector('[data-quantity-increase]');
    const coverName = selector.querySelector('[data-cover-name]');

    if (!(variantInput instanceof HTMLInputElement)) return;

    const getActiveOption = () => options.find((option) => option.classList.contains('is-active')) || null;

    const getMaxForOption = (option) => {
      if (!(option instanceof HTMLElement)) return null;
      const rawMax = option.dataset.variantMax;
      if (rawMax === undefined || rawMax === null || rawMax === '') return null;

      const maxValue = Number(rawMax);
      if (!Number.isFinite(maxValue)) return null;

      const cartQty = Number(option.dataset.variantCart || 0);
      const remaining = maxValue - cartQty;
      return remaining > 0 ? remaining : 0;
    };

    const setQuantityValue = (next) => {
      if (!(quantityInput instanceof HTMLInputElement) || !(quantityValue instanceof HTMLElement)) return;

      const safeValue = Math.max(1, next);
      quantityInput.value = String(safeValue);
      quantityValue.textContent = String(safeValue);
    };

    const updateQuantityButtons = (max) => {
      if (!(quantityInput instanceof HTMLInputElement)) return;
      if (!(quantityDecrease instanceof HTMLButtonElement)) return;
      if (!(quantityIncrease instanceof HTMLButtonElement)) return;

      const current = Number(quantityInput.value) || 1;
      quantityDecrease.disabled = current <= 1;

      if (max === null) {
        quantityIncrease.disabled = false;
        return;
      }

      quantityIncrease.disabled = current >= max;
    };

    const clampQuantityToMax = (option) => {
      if (!(quantityInput instanceof HTMLInputElement)) return null;
      const max = getMaxForOption(option);
      const current = Number(quantityInput.value) || 1;

      if (max === null) {
        updateQuantityButtons(null);
        return null;
      }

      const next = Math.min(Math.max(1, current), Math.max(1, max));
      setQuantityValue(next);
      updateQuantityButtons(max);
      return max;
    };

    /** @param {number} delta */
    const updateQuantity = (delta) => {
      if (!(quantityInput instanceof HTMLInputElement)) return;

      const current = Number(quantityInput.value) || 1;
      const option = getActiveOption();
      const max = getMaxForOption(option);
      let next = current + delta;

      if (max !== null) {
        next = Math.min(next, Math.max(1, max));
      }

      setQuantityValue(next);
      updateQuantityButtons(max);
    };

    if (quantityDecrease instanceof HTMLElement) {
      quantityDecrease.addEventListener('click', () => updateQuantity(-1));
    }

    if (quantityIncrease instanceof HTMLElement) {
      quantityIncrease.addEventListener('click', () => updateQuantity(1));
    }

    /** @param {Element} option */
    const updateCoverName = (option) => {
      if (!(coverName instanceof HTMLElement)) return;

      const coverIndex = Number(option.getAttribute('data-cover-index')) || options.indexOf(option) + 1;
      coverName.textContent = `COVER ${coverIndex}`;
    };

    const getCoverState = (option) => {
      if (!(option instanceof HTMLElement)) {
        return { isAvailable: false, hasMedia: false };
      }

      const isAvailable = option.dataset.coverAvailable === 'true';
      const hasMedia = option.dataset.coverHasMedia === 'true' || !!option.querySelector('img');

      return { isAvailable, hasMedia };
    };

    const updateAddButtonState = (option) => {
      if (!(addButton instanceof HTMLButtonElement)) return;
      if (!(option instanceof HTMLElement)) return;

      const { isAvailable, hasMedia } = getCoverState(option);
      const max = getMaxForOption(option);

      if (max !== null && max <= 0) {
        addButton.disabled = true;
        addButton.textContent = 'SOLD OUT';
        return;
      }

      if (!hasMedia) {
        addButton.disabled = true;
        addButton.textContent = 'NO COVER';
        return;
      }

      if (!isAvailable) {
        addButton.disabled = true;
        addButton.textContent = 'SOLD OUT';
        return;
      }

      addButton.disabled = false;
      addButton.textContent = 'BUY';
    };

    const animateCoverWidth = (/** @type {HTMLElement} */ targetOption) => {
      const widthTransition = 'width 1.6s cubic-bezier(0.22, 1, 0.36, 1)';

      // 1. Lock current computed widths and set transition
      options.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        item.style.width = getComputedStyle(item).width;
        item.style.transition = widthTransition;
      });

      // 2. Update classes — CSS targets change, but inline style still overrides
      options.forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-pressed', 'false');
      });
      targetOption.classList.add('is-active');
      targetOption.setAttribute('aria-pressed', 'true');

      // 3. double rAF: remove inline width lock → browser transitions from locked px to CSS value
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          options.forEach((item) => {
            if (!(item instanceof HTMLElement)) return;
            item.style.removeProperty('width');
          });
        });
      });
    };

    options.forEach((option) => {
      option.addEventListener('click', () => {
        const variantId = option.getAttribute('data-variant-id');
        if (!variantId) return;

        variantInput.value = variantId;
        updateCoverName(option);
        animateCoverWidth(option);
        updateAddButtonState(option);
        clampQuantityToMax(option);
      });
    });

    const isPurchasableOption = (option) => {
      if (!(option instanceof HTMLElement)) return false;
      const { isAvailable, hasMedia } = getCoverState(option);
      return isAvailable && hasMedia;
    };

    let activeOption = options.find((option) => option.classList.contains('is-active')) || null;

    if (!isPurchasableOption(activeOption)) {
      activeOption = options.find(isPurchasableOption) || null;
      if (activeOption) {
        options.forEach((item) => {
          item.classList.remove('is-active');
          item.setAttribute('aria-pressed', 'false');
        });
        activeOption.classList.add('is-active');
        activeOption.setAttribute('aria-pressed', 'true');
      }
    }

    if (activeOption) {
      updateCoverName(activeOption);
      updateAddButtonState(activeOption);
      clampQuantityToMax(activeOption);
    } else if (options[0]) {
      updateAddButtonState(options[0]);
      clampQuantityToMax(options[0]);
    }

    if (form instanceof HTMLFormElement && addButton instanceof HTMLButtonElement && form.dataset.initialized !== 'true') {
      form.dataset.initialized = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (addButton instanceof HTMLButtonElement && addButton.disabled) return;

        const formData = new FormData(form);
        const activeForSubmit = getActiveOption();
        const maxForSubmit = getMaxForOption(activeForSubmit);
        let quantity = Number(formData.get('quantity')) || 1;

        if (maxForSubmit !== null) {
          if (maxForSubmit <= 0) return;
          quantity = Math.min(quantity, maxForSubmit);
          formData.set('quantity', String(quantity));
        }

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionIds = [];
        for (const item of cartItemsComponents) {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionIds.push(item.dataset.sectionId);
          }
        }

        if (sectionIds.length > 0) {
          formData.set('sections', sectionIds.join(','));
          formData.set('sections_url', window.location.pathname);
        }

        const previousText = addButton.textContent;
        addButton.disabled = true;
        addButton.textContent = 'ADDING...';

        const refreshAddButtonState = () => {
          const activeNow = getActiveOption();
          if (activeNow instanceof HTMLElement) {
            updateAddButtonState(activeNow);
            clampQuantityToMax(activeNow);
          } else if (options[0] instanceof HTMLElement) {
            updateAddButtonState(options[0]);
            clampQuantityToMax(options[0]);
          }
        };

        try {
          const response = await fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
            },
            body: formData,
          });

          const json = await response.json();
          if (!response.ok || json.status) {
            throw new Error(json.description || json.message || 'Failed to add item to cart');
          }

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: json,
                sourceId: 'magazine-cover',
                data: {
                  source: 'product-form-component',
                  itemCount: quantity,
                  variantId: String(formData.get('id') || ''),
                  sections: json.sections,
                },
              },
            })
          );

          if (activeForSubmit instanceof HTMLElement) {
            const currentCartQty = Number(activeForSubmit.dataset.variantCart || 0);
            activeForSubmit.dataset.variantCart = String(currentCartQty + quantity);
            updateAddButtonState(activeForSubmit);
            clampQuantityToMax(activeForSubmit);
          }

          addButton.textContent = 'ADDED';
          window.setTimeout(() => {
            refreshAddButtonState();
            if (!addButton.disabled) {
              addButton.textContent = previousText;
            }
          }, 900);
        } catch (error) {
          console.error(error);
          addButton.textContent = 'ERROR';
          window.setTimeout(() => {
            refreshAddButtonState();
            if (!addButton.disabled) {
              addButton.textContent = previousText;
            }
          }, 1200);
        } finally {
          refreshAddButtonState();
        }
      });
    }
  });

  applyLazyFallback();
  animateImage();
  highlightChapter();

  document.addEventListener('magazine:refresh', () => {
    highlightChapter();
    if (!animationActive) {
      animationActive = true;
      animateImage();
    }
  });

  window.addEventListener('scroll', throttledHighlightChapter);
  window.addEventListener('resize', throttledHighlightChapter);
  window.addEventListener('load', throttledHighlightChapter);
})();
